import type { PlannedMeal } from '@/types/meal-plan'
import type { Recipe } from '@/types/recipe'
import { ingredientParser, normalizeIngredientName, type ShoppingListItem } from './parser'
import {
  UNIT_CONVERSIONS,
  categorizeIngredient as categorizeIngredientByKeyword,
  getCategoryDisplayName as getCategoryLabel,
  groupByCategory as buildCategoryGroups,
  type AggregatedShoppingItem,
  type ShoppingListCategoryGroup,
} from './categories'
import { exportMealPlanShoppingList } from './export'

export interface MealPlanShoppingListData {
  meal_plan_id: string
  date_range: { start_date: string; end_date: string }
  total_meals: number
  total_recipes: number
  categories: ShoppingListCategoryGroup[]
  all_items: AggregatedShoppingItem[]
  generated_at: string
}

export class IngredientAggregator {
  
  /**
   * Aggregate ingredients from multiple planned meals into a unified shopping list
   */
  aggregateFromPlannedMeals(
    plannedMeals: PlannedMeal[], 
    recipes: Recipe[],
    dateRange?: { start_date: string; end_date: string }
  ): MealPlanShoppingListData {
    
    // Filter meals by date range if provided
    let filteredMeals = plannedMeals
    if (dateRange) {
      filteredMeals = plannedMeals.filter(meal => 
        meal.planned_date >= dateRange.start_date && meal.planned_date <= dateRange.end_date
      )
    }
    
    // Group meals by recipe to handle scaling
    const mealsByRecipe = new Map<string, PlannedMeal[]>()
    for (const meal of filteredMeals) {
      if (!meal.recipe_id) continue
      
      if (!mealsByRecipe.has(meal.recipe_id)) {
        mealsByRecipe.set(meal.recipe_id, [])
      }
      mealsByRecipe.get(meal.recipe_id)!.push(meal)
    }
    
    // Aggregate ingredients
    const aggregatedItems = new Map<string, AggregatedShoppingItem>()
    
    for (const [recipeId, meals] of mealsByRecipe) {
      const recipe = recipes.find(r => r.id === recipeId)
      if (!recipe || !recipe.ingredients) continue
      
      // Calculate total servings needed for this recipe
      const totalServings = meals.reduce((sum, meal) => sum + (meal.planned_servings || recipe.servings || 4), 0)
      const servingsFactor = totalServings / (recipe.servings || 4)
      
      // Parse and scale ingredients
      const scaledIngredients = this.scaleRecipeIngredients(recipe.ingredients, servingsFactor)
      const parsedItems = ingredientParser.parseIngredientsToShoppingList(scaledIngredients)
      
      for (const item of parsedItems) {
        const itemKey = this.generateItemKey(item.name)
        
        if (aggregatedItems.has(itemKey)) {
          // Merge with existing item
          const existing = aggregatedItems.get(itemKey)!
          this.mergeShoppingItems(existing, item, recipe)
        } else {
          // Create new aggregated item
          const category = categorizeIngredientByKeyword(item.name)
          const aggregatedItem: AggregatedShoppingItem = {
            ...item,
            recipe_ids: [recipeId],
            recipe_titles: [recipe.title],
            category,
            servings_factor: servingsFactor,
            original_quantities: [{
              recipeId,
              quantity: item.quantity,
              unit: item.unit
            }],
            is_purchased: false
          }
          aggregatedItems.set(itemKey, aggregatedItem)
        }
      }
    }
    
    // Group by categories
    const categoryGroups = this.groupByCategory(Array.from(aggregatedItems.values()))
    
    // Determine date range
    const finalDateRange = dateRange || this.calculateDateRange(filteredMeals)
    
    return {
      meal_plan_id: filteredMeals[0]?.meal_plan_id || '',
      date_range: finalDateRange,
      total_meals: filteredMeals.length,
      total_recipes: mealsByRecipe.size,
      categories: categoryGroups,
      all_items: Array.from(aggregatedItems.values()),
      generated_at: new Date().toISOString()
    }
  }
  
  /**
   * Scale recipe ingredients by a factor
   */
  private scaleRecipeIngredients(ingredients: string[], factor: number): string[] {
    if (factor === 1) return ingredients
    
    return ingredients.map(ingredient => {
      const parsed = ingredientParser.parseIngredient(ingredient)
      
      if (parsed.quantity && typeof parsed.quantity === 'number') {
        const scaledQuantity = Math.round((parsed.quantity * factor) * 100) / 100
        let result = `${scaledQuantity}`
        
        if (parsed.unit) {
          result += ` ${parsed.unit}`
        }
        
        result += ` ${parsed.item}`
        
        if (parsed.specification) {
          result += ` (${parsed.specification})`
        }
        
        return result
      }
      
      return ingredient
    })
  }
  
  /**
   * Generate a consistent key for grouping similar items
   */
  private generateItemKey(itemName: string): string {
    return normalizeIngredientName(itemName)
  }
  
  /**
   * Merge two shopping items with smart quantity aggregation
   */
  private mergeShoppingItems(
    existing: AggregatedShoppingItem, 
    newItem: ShoppingListItem, 
    recipe: Recipe
  ): void {
    // Add recipe information
    if (!existing.recipe_ids.includes(recipe.id)) {
      existing.recipe_ids.push(recipe.id)
      existing.recipe_titles.push(recipe.title)
    }
    
    // Track original quantities
    existing.original_quantities = existing.original_quantities || []
    existing.original_quantities.push({
      recipeId: recipe.id,
      quantity: newItem.quantity,
      unit: newItem.unit
    })
    
    // Aggregate quantities if units are compatible
    if (existing.quantity && newItem.quantity && existing.unit && newItem.unit) {
      const mergedQuantity = this.mergeQuantities(
        existing.quantity, existing.unit,
        newItem.quantity, newItem.unit
      )
      
      if (mergedQuantity) {
        existing.quantity = mergedQuantity.quantity
        existing.unit = mergedQuantity.unit
      }
    } else if (newItem.quantity && !existing.quantity) {
      existing.quantity = newItem.quantity
      existing.unit = newItem.unit
    }
    
    // Merge specifications
    if (newItem.specification && !existing.specification?.includes(newItem.specification)) {
      existing.specification = existing.specification 
        ? `${existing.specification}, ${newItem.specification}`
        : newItem.specification
    }
  }
  
  /**
   * Merge quantities with unit conversion
   */
  private mergeQuantities(
    qty1: number, unit1: string,
    qty2: number, unit2: string
  ): { quantity: number; unit: string } | null {
    
    // If units are the same, just add
    if (unit1 === unit2) {
      return { quantity: qty1 + qty2, unit: unit1 }
    }
    
    // Try to convert to common unit
    const conversion1 = UNIT_CONVERSIONS[unit1]
    const conversion2 = UNIT_CONVERSIONS[unit2]
    
    if (conversion1 && conversion2) {
      // Convert both to base unit and add
      const baseQty1 = qty1 * conversion1
      const baseQty2 = qty2 * conversion2
      const totalBase = baseQty1 + baseQty2
      
      // Choose best unit for display
      const bestUnit = this.chooseBestUnit(totalBase, unit1, unit2)
      const bestConversion = UNIT_CONVERSIONS[bestUnit]
      
      return {
        quantity: Math.round((totalBase / bestConversion) * 100) / 100,
        unit: bestUnit
      }
    }
    
    // Can't merge, keep first quantity
    return null
  }
  
  /**
   * Choose the best unit for displaying merged quantities
   */
  private chooseBestUnit(totalInBase: number, unit1: string, unit2: string): string {
    // Volume units (in ml)
    if (['ml', 'cl', 'dl', 'liter', 'l'].includes(unit1) || ['ml', 'cl', 'dl', 'liter', 'l'].includes(unit2)) {
      if (totalInBase >= 1000) return 'liter'
      if (totalInBase >= 100) return 'dl'
      if (totalInBase >= 10) return 'cl'
      return 'ml'
    }
    
    // Weight units (in grams)
    if (['gram', 'g', 'gr', 'kilogram', 'kg', 'kilo'].includes(unit1) || ['gram', 'g', 'gr', 'kilogram', 'kg', 'kilo'].includes(unit2)) {
      if (totalInBase >= 1000) return 'kg'
      return 'gram'
    }
    
    // Default to first unit
    return unit1
  }
  
  /**
   * Categorize ingredient into predefined categories
   */
  categorizeIngredient(itemName: string): string {
    return categorizeIngredientByKeyword(itemName)
  }
  
  /**
   * Group items by category
   */
  groupByCategory(items: AggregatedShoppingItem[]): ShoppingListCategoryGroup[] {
    return buildCategoryGroups(items)
  }
  
  /**
   * Calculate date range from planned meals
   */
  private calculateDateRange(meals: PlannedMeal[]): { start_date: string; end_date: string } {
    if (meals.length === 0) {
      const today = new Date().toISOString().split('T')[0]
      return { start_date: today, end_date: today }
    }
    
    const dates = meals.map(meal => meal.planned_date).sort()
    return {
      start_date: dates[0],
      end_date: dates[dates.length - 1]
    }
  }
  
  /**
   * Get category display name in Dutch
   */
  getCategoryDisplayName(category: string): string {
    return getCategoryLabel(category)
  }
  
  /**
   * Export aggregated shopping list to different formats
   */
  exportToFormat(
    shoppingData: MealPlanShoppingListData, 
    format: 'text' | 'csv' | 'json'
  ): string {
    return exportMealPlanShoppingList(shoppingData, format)
  }
}

// Export singleton instance
export const ingredientAggregator = new IngredientAggregator()
export type { AggregatedShoppingItem, ShoppingListCategoryGroup }
